import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Grid, Input, Dropdown, Menu } from 'semantic-ui-react';
import { connect, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import unassignIcon from '../../icons/unassign@3x.png';
import deleteIcon from '../../icons/del.png';
import {
  EditMode,
  PLanningMode,
  VIEWS,
  schedulerViewSVGIcon,
  MapViewSVGIcon,
  gridViewSVGIcon,
  recurringTaskSVGIcon,
  ACTION_MENU,
  THEME_COLOUR,
  arrowDownSvg,
  MM_DD_YYYY_FORMAT,
} from '../../HelperMethods/constants';
import * as UserUtilities from '../../HelperMethods/UserUtilities';
import { ENTITY_ACCESS_TYPE, RESOURCE_TYPE, RESOURCES } from '../../HelperMethods/role-resource-constants';
import { BLANK_STRING } from '../../HelperMethods/AgGridConstants';
import PublishButtonGroup from './PublishButtonGroup';
import DateRangeComponent from '../common/DateRange';
import Joyn2Button from '../common/Joyn2Button';
import EngineRunCheckModal from './modals/EngineRunCheckModal';
import { editSvg } from '../../common/constants';

const isFilterSelected = (taskListGridApi, field, value) => {
  if (taskListGridApi) {
    const filterModel = taskListGridApi.getFilterModel();
    if (filterModel && Object.keys(filterModel).length === 1) {
      const filterObj = filterModel[field] || filterModel[`${field}_1`];
      if (filterObj) {
        if (filterObj.type === 'equals' && filterObj.filter === value) {
          return true;
        }
      }
    }
  }
  return false;
};

const dateRangeValidation = (range) => {
  const [dates] = range;
  const { endDate, startDate } = dates || {};
  const diffDays = moment(endDate).diff(startDate, 'days');
  return diffDays <= 7
    ? { isValid: true }
    : { isValid: false, message: 'Maximum Selected date range should be 7 days' };
};

const engineRunCheck = (tasksEditMode, setrunEngineCheckModal) => {
  if (tasksEditMode === EditMode.UNPUBLISHED) {
    // toastr.warning('kindly discard/publish the engine result');
    setrunEngineCheckModal(true);
    return false;
  }
  return true;
};

const imgStyle = {
  width: '24px',
  height: '24px',
  margin: 0,
  maxHeight: 'none',
  maxWidth: 'none',
  filter: 'invert(100%) sepia(100%) saturate(0%) hue-rotate(1deg) brightness(102%) contrast(101%)',
};

const buttonStyle = {
  background: '#424f62',
  border: '1px solid #4c4c4c',
  padding: '2px',
  marginRight: '2px',
  marginTop: 0,
};

const BulkButtons = ({ handleBulkEdit, onTaskBulkDelete, onTaskBulkUnassigned, currentView, selectedTaskLength }) => {
  return (
    <div
      id="header-buttons"
      className="bulk"
      style={currentView === VIEWS.recurringTaskGridView ? { marginTop: '10px', marginRight: '10px' } : {}}
    >
      <Button.Group basic>
        {selectedTaskLength > 1 && (
          <Button icon className="header-item" onClick={handleBulkEdit} style={{ width: '44px' }}>
            <i
              className="fa fa-pencil-square-o"
              // style={{ color: 'rgba(108,108,108,0.7)', fontSize: '20px', paddingTop: '2px' }}
            />
            <div className="item-label">Bulk Edit</div>
          </Button>
        )}
        <Button icon className="header-item" onClick={onTaskBulkDelete} style={{ width: '44px' }}>
          <img src={deleteIcon} alt="Delete" />
          <div className="item-label">Delete</div>
        </Button>
        {currentView !== VIEWS.recurringTaskGridView && (
          <Button icon className="header-item" onClick={onTaskBulkUnassigned} style={{ width: '44px' }}>
            <img src={unassignIcon} alt="Unassign" />
            <div className="item-label">Unassign</div>
          </Button>
        )}
      </Button.Group>
    </div>
  );
};

const TaskHeader = React.memo(function TaskHeader({
  selectedUsers,
  runEngine,
  openEngineSettings,
  // openAutomateDailyRunModal,
  currentView,
  setView,
  selectedDateRange,
  onSelectedDateChange,
  headerHeight,
  tasksEditMode,
  tasksPlanningState,
  onPublish,
  onDiscardPublish,
  onTaskBulkDelete,
  areTasksSelected,
  onTaskBulkUnassigned,
  kpi,
  applyFilter,
  taskListGridApi,
  planningModeEnabled,
  loggedInUser,
  handleBulkEdit,
  selectedTaskLength,
}) {
  const history = useHistory();
  const settings = useSelector((state) => state.appReducer.settings);
  const packagesInfo = useSelector((state) => state.appReducer.packagesInfo);
  const planningEngineEnabled = !!(settings && settings.featureFlags && settings.featureFlags.planningEngineEnabled);
  const [runEngineCheckModal, setrunEngineCheckModal] = useState(false);
  const menuItems = [
    {
      ...ACTION_MENU.CREATE_TASK,
      onClick: () => {
        history.push('/taskViewPage/task/new');
      },
      renderCondition: (user) =>
        UserUtilities.determineIfUserHasAccessToResource({
          resourceType: RESOURCE_TYPE.ENTITY,
          resource: RESOURCES.ENTITY.TASK,
          entityAccessType: ENTITY_ACCESS_TYPE.CREATE,
          user,
          packageName: settings?.package,
          packagesInfo,
        }),
    },
    {
      ...ACTION_MENU.BULK_CREATE_TASKS,
      onClick: () => {
        history.push('/taskViewPage/task/bulknew');
      },
      renderCondition: (user) =>
        UserUtilities.determineIfUserHasAccessToResource({
          resourceType: RESOURCE_TYPE.ENTITY,
          resource: RESOURCES.ENTITY.TASK,
          entityAccessType: ENTITY_ACCESS_TYPE.CREATE,
          user,
          packageName: settings?.package,
          packagesInfo,
        }),
    },
    {
      ...ACTION_MENU.RUN_DAILY_ENGINE,
      onClick: () => {
        if (engineRunCheck(tasksEditMode, setrunEngineCheckModal)) runEngine();
      },
      renderCondition: (user) =>
        UserUtilities.determineIfUserHasAccessToResource({
          resourceType: RESOURCE_TYPE.FEATURE,
          resource: RESOURCES.FEATURE.ROUTING_ENGINE,
          user,
          packageName: settings?.package,
          packagesInfo,
        }),
    },
    {
      ...ACTION_MENU.DAILY_ENGINE_SETTINGS,
      onClick: () => {
        openEngineSettings(false);
      },
      renderCondition: (user) =>
        planningModeEnabled &&
        UserUtilities.determineIfUserHasAccessToResource({
          resourceType: RESOURCE_TYPE.FEATURE,
          resource: RESOURCES.FEATURE.ROUTING_ENGINE,
          user,
          packageName: settings?.package,
          packagesInfo,
        }),
    },
    // {
    //   ...ACTION_MENU.AUTOMATE_DAILY_ENGINE_RUN,
    //   onClick: () => {
    //     openAutomateDailyRunModal();
    //   },
    //   renderCondition: (user) =>
    //     UserUtilities.determineIfUserHasAccessToResource({
    //       resourceType: RESOURCE_TYPE.FEATURE,
    //       resource: RESOURCES.FEATURE.ROUTING_ENGINE,
    //       user,
    //     }),
    // },
    {
      ...ACTION_MENU.PLANNING_ENGINE_SETTINGS,
      onClick: () => {
        openEngineSettings(true);
      },
      renderCondition: (user) =>
        UserUtilities.determineIfUserHasAccessToResource({
          resourceType: RESOURCE_TYPE.FEATURE,
          resource: RESOURCES.FEATURE.PLANNING_ENGINE,
          user,
          packageName: settings?.package,
          packagesInfo,
        }) && planningEngineEnabled,
    },
    {
      ...ACTION_MENU.ENGINE_SIMULATOR,
      onClick: () => {
        history.push('/taskViewPage/runEngineView');
      },
      renderCondition: () => {
        return false;
      },
    },
    {
      ...ACTION_MENU.ENGINE_LOG,
      onClick: () => {
        history.push('/taskViewPage/engineLogView');
      },
      renderCondition: (user) =>
        UserUtilities.determineIfUserHasAccessToResource({
          resourceType: RESOURCE_TYPE.FEATURE,
          resource: RESOURCES.FEATURE.ROUTING_ENGINE,
          user,
          packageName: settings?.package,
          packagesInfo,
        }),
    },
  ];
  const [showCalendar, handleShowCalendar] = useState(false);
  const [calendarLeft, handleCalendarLeftPosition] = useState(0);

  const handleToggleCalendar = () => {
    handleShowCalendar((prevShowCalendar) => !prevShowCalendar);
  };

  const handleDateRangeSelected = (range) => {
    handleToggleCalendar();
    onSelectedDateChange(range);
  };

  useEffect(() => {
    if (showCalendar) {
      const calendarDate = document.getElementById('calendar-date');
      if (!calendarDate) handleCalendarLeftPosition(0);
      const calendarDateRect = calendarDate.getBoundingClientRect();
      const dateRangeComponent = document.getElementById('date-range-component');
      if (!dateRangeComponent) handleCalendarLeftPosition(0);
      const dateRangeComponentRect = dateRangeComponent.getBoundingClientRect();
      const diff = calendarDateRect.right - dateRangeComponentRect.right;
      handleCalendarLeftPosition(`${diff}px`);
    } else {
      handleCalendarLeftPosition(0);
    }
  }, [showCalendar]);

  const menuItemsToDisplay = menuItems.filter((menuItem) => menuItem.renderCondition(loggedInUser));
  return (
    <>
      <Grid style={{ margin: '0' }}>
        <Grid.Row
          verticalAlign="middle"
          className="header-row-container"
          style={{
            paddingTop: '5px',
            paddingBottom: '5px',
            height: headerHeight,
            flexWrap: 'nowrap',
          }}
        >
          {currentView === VIEWS.schedulerView && <Grid.Column width={3} id="portal-resource-tab" />}
          <Grid.Column width={2} style={{ display: 'flex', flexDirection: 'row', whiteSpace: 'nowrap' }}>
            <div id="header-buttons">
              <Button.Group>
                <Button
                  icon
                  className={`header-item ${currentView === VIEWS.mapView ? 'selectedView' : ''}`}
                  onClick={() => setView(VIEWS.mapView)}
                  style={{ width: '44px' }}
                >
                  <div className="item-label">Map View</div>
                  <img
                    src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(MapViewSVGIcon)}`}
                    className="link-to-views"
                    alt="Map View"
                  />
                </Button>
                <Button
                  icon
                  className={`header-item ${currentView === VIEWS.taskGridView ? 'selectedView' : ''}`}
                  onClick={() => setView(VIEWS.taskGridView)}
                  style={{ width: '44px' }}
                  id="grid-view-button"
                >
                  <div className="item-label">Grid View</div>
                  <img
                    src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(gridViewSVGIcon)}`}
                    className="link-to-views"
                    alt="Grid View"
                  />
                </Button>
                <Button
                  icon
                  className={`header-item ${currentView === VIEWS.schedulerView ? 'selectedView' : ''}`}
                  onClick={() => setView(VIEWS.schedulerView)}
                  style={{ width: '44px' }}
                >
                  <div className="item-label">Scheduler View</div>
                  <img
                    src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(schedulerViewSVGIcon)}`}
                    className="link-to-views"
                    alt="Scheduler View"
                  />
                </Button>
              </Button.Group>
            </div>
          </Grid.Column>
          {currentView !== VIEWS.schedulerView && (
            <>
              {/* this Grid Columns is showing the name of the user selected from the user list at the left hand side of task view */}
              <Grid.Column
                width={2}
                style={{
                  textAlign: 'center',
                  textOverflow: 'ellipsis',
                  overflow: currentView === VIEWS.taskGridView ? 'hidden' : 'unset',
                }}
              >
                {selectedUsers.length === 1 ? `${selectedUsers[0].firstName} ${selectedUsers[0].lastName}` : ``}
              </Grid.Column>
              {currentView === VIEWS.mapView && (
                <Grid.Column
                  width={7}
                  className="runDropdown"
                  style={{
                    textAlign: 'center',
                    textOverflow: 'ellipsis',
                    overflow: currentView === VIEWS.taskGridView ? 'hidden' : 'unset',
                  }}
                >
                  <div
                    id="portal-elements"
                    style={{ display: 'flex', flexGrow: '1', marginTop: '11px', marginBottom: '11px' }}
                  />
                </Grid.Column>
              )}

              <Grid.Column width={5}>
                {currentView === VIEWS.taskGridView && (
                  <Grid>
                    <Grid.Row className="tasks-count-container" columns={5}>
                      <Grid.Column
                        className="tasks"
                        onClick={() => {
                          applyFilter(null);
                        }}
                      >
                        <div className="count">{kpi.totalTasks}</div>
                        <div className="text">Total</div>
                      </Grid.Column>
                      <Grid.Column
                        className={`tasks ${isFilterSelected(taskListGridApi, 'status', 'open') ? 'selected' : ''}`}
                        onClick={() => {
                          applyFilter('status', 'open');
                        }}
                      >
                        <div className="count">{kpi.openTasks}</div>
                        <div className="text">Open</div>
                      </Grid.Column>
                      <Grid.Column
                        className={`tasks ${
                          isFilterSelected(taskListGridApi, 'assignedToUserName', BLANK_STRING) ? 'selected' : ''
                        }`}
                        onClick={() => {
                          applyFilter('assignedToUserName', BLANK_STRING);
                        }}
                      >
                        <div className="count">{kpi.unAssignedTasks}</div>
                        <div className="text">Unassigned</div>
                      </Grid.Column>
                      <Grid.Column
                        className={`tasks ${isFilterSelected(taskListGridApi, 'status', 'draft') ? 'selected' : ''}`}
                        onClick={() => {
                          applyFilter('status', 'draft');
                        }}
                      >
                        <div className="count">{kpi.draftTasks}</div>
                        <div className="text">Draft</div>
                      </Grid.Column>
                      <Grid.Column
                        className={`tasks ${isFilterSelected(taskListGridApi, 'status', 'resolved') ? 'selected' : ''}`}
                        onClick={() => {
                          applyFilter('status', 'resolved');
                        }}
                      >
                        <div className="count">{kpi.resolvedTasks}</div>
                        <div className="text">Resolved</div>
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                )}
              </Grid.Column>

              <Grid.Column
                width={tasksEditMode === EditMode.UNPUBLISHED ? 3 : 2}
                textAlign="right"
                className="action-buttons-div"
              >
                {tasksEditMode === EditMode.UNPUBLISHED && tasksPlanningState !== PLanningMode.PLANNINGINPROGRESS && (
                  <>
                    {areTasksSelected && (
                      <>
                        {/* <Button
                          style={{ background: '#424f62', border: '1px solid #4c4c4c', padding: '8px' }}
                          onClick={handleBulkEdit}
                          icon
                          className="header-item"
                        >
                          <i className="fa fa-edit" style={{ color: 'white' }} />
                          <div className="item-label">Bulk Edit</div>
                        </Button> */}

                        {selectedTaskLength > 1 && (
                          <Button onClick={handleBulkEdit} style={buttonStyle} className="header-item">
                            <img
                              src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(editSvg)}`}
                              alt="Discard"
                              style={imgStyle}
                            />
                            <div className="item-label">Bulk Edit</div>
                          </Button>
                        )}
                      </>
                    )}
                    <PublishButtonGroup onPublish={onPublish} onDiscardPublish={onDiscardPublish} />
                  </>
                )}
                {currentView !== VIEWS.recurringTaskGridView &&
                  tasksEditMode === EditMode.PUBLISHED &&
                  areTasksSelected && (
                    <BulkButtons
                      handleBulkEdit={handleBulkEdit}
                      onTaskBulkUnassigned={onTaskBulkUnassigned}
                      onTaskBulkDelete={onTaskBulkDelete}
                      selectedTaskLength={selectedTaskLength}
                    />
                  )}
              </Grid.Column>
              {currentView !== VIEWS.recurringTaskGridView && <div id="filter-button" style={{ marginTop: '10px' }} />}
              <Grid.Column width={4}>
                {currentView !== VIEWS.recurringTaskGridView && currentView !== VIEWS.schedulerView && (
                  <div style={{ display: 'flex' }} id="calendar-date">
                    <label style={{ margin: '10px' }}>Date</label>
                    <Input
                      value={
                        selectedDateRange.startDate === selectedDateRange.endDate
                          ? `${moment(selectedDateRange.startDate).format(MM_DD_YYYY_FORMAT)}`
                          : `${moment(selectedDateRange.startDate).format(MM_DD_YYYY_FORMAT)} - ${moment(
                              selectedDateRange.endDate
                            ).format(MM_DD_YYYY_FORMAT)}`
                      }
                      readOnly
                      style={{ width: '100%', fontSize: '12px' }}
                      onClick={
                        tasksEditMode === EditMode.UNPUBLISHED && tasksPlanningState === PLanningMode.PLANNINGPUBLISHED
                          ? null
                          : handleToggleCalendar
                      }
                      icon={
                        tasksEditMode === EditMode.UNPUBLISHED && tasksPlanningState === PLanningMode.PLANNINGPUBLISHED
                          ? ''
                          : 'calendar outline'
                      }
                    />
                    {showCalendar && (
                      <div
                        style={{
                          position: 'absolute',
                          zIndex: 100,
                          top: '40px',
                          left: calendarLeft,
                        }}
                        id="date-range-component"
                      >
                        <DateRangeComponent
                          callback={handleDateRangeSelected}
                          range={{
                            endDate: new Date(moment(selectedDateRange.endDate).format()),
                            startDate: new Date(moment(selectedDateRange.startDate).format()),
                          }}
                          handleShowCalendar={handleToggleCalendar}
                          validation={dateRangeValidation}
                        />
                      </div>
                    )}
                  </div>
                )}
              </Grid.Column>
              {currentView === VIEWS.recurringTaskGridView &&
                tasksEditMode === EditMode.PUBLISHED &&
                areTasksSelected && (
                  <BulkButtons
                    handleBulkEdit={handleBulkEdit}
                    onTaskBulkUnassigned={onTaskBulkUnassigned}
                    onTaskBulkDelete={onTaskBulkDelete}
                    currentView={currentView}
                    selectedTaskLength={selectedTaskLength}
                  />
                )}
              {currentView === VIEWS.recurringTaskGridView && (
                <div id="filter-button" style={{ marginTop: '12px', marginRight: '10px' }} />
              )}
              <div
                className={`header-item ${currentView === VIEWS.recurringTaskGridView ? 'selectedView' : ''}`}
                style={{
                  marginTop: currentView === VIEWS.recurringTaskGridView ? '0px' : '10px',
                }}
              >
                <Joyn2Button
                  margin={currentView === VIEWS.recurringTaskGridView ? '18px 0 0 0' : '0px 0 0 0'}
                  width="32px"
                  size="large"
                  svg={{
                    icon: recurringTaskSVGIcon(THEME_COLOUR.LIGHT),
                  }}
                  onClick={() => setView(VIEWS.recurringTaskGridView)}
                />

                <div className="item-label" style={{ whiteSpace: 'nowrap', left: '10%' }}>
                  Recurring Task View
                </div>
              </div>
            </>
          )}
          {currentView === VIEWS.schedulerView && (
            <>
              <div
                id="portal-elements"
                style={{ display: 'flex', flexGrow: '1', marginTop: '11px', marginBottom: '11px' }}
              />
            </>
          )}
          <>
            <Menu style={{ margin: '11px 4px' }} id="task-header-dropdown">
              <Dropdown
                item
                text="Actions"
                icon={
                  <img
                    src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(arrowDownSvg())}`}
                    style={{ marginLeft: '5px', paddingLeft: '7px' }}
                  />
                }
              >
                <Dropdown.Menu>
                  <>
                    {menuItemsToDisplay.map((menuItem) => (
                      <Dropdown.Item onClick={menuItem.onClick} key={menuItem.key}>
                        {menuItem.text}
                      </Dropdown.Item>
                    ))}
                  </>
                </Dropdown.Menu>
              </Dropdown>
            </Menu>
          </>
        </Grid.Row>
      </Grid>
      <div>
        <EngineRunCheckModal
          open={runEngineCheckModal}
          onYesClick={async () => {
            await onDiscardPublish();
            setrunEngineCheckModal(false);
            runEngine();
          }}
          onNoClick={() => setrunEngineCheckModal(false)}
        />
      </div>
    </>
  );
});

TaskHeader.propTypes = {
  draftTasks: PropTypes.number,
  currentView: PropTypes.string.isRequired,
  setView: PropTypes.func.isRequired,
  runEngine: PropTypes.func.isRequired,
  openEngineSettings: PropTypes.func.isRequired,
  onPublish: PropTypes.func.isRequired,
  tasksEditMode: PropTypes.string.isRequired,
  onDiscardPublish: PropTypes.func.isRequired,
  selectedDateRange: PropTypes.shape({
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
  }).isRequired,
  // openAutomateDailyRunModal: PropTypes.func.isRequired,
  selectedUsers: PropTypes.array,
  headerHeight: PropTypes.string,
  tasksPlanningState: PropTypes.string.isRequired,
  onTaskBulkDelete: PropTypes.func.isRequired,
  areTasksSelected: PropTypes.bool.isRequired,
  selectedTaskLength: PropTypes.number.isRequired,
  kpi: PropTypes.object,
  applyFilter: PropTypes.func.isRequired,
  taskListGridApi: PropTypes.object,
  planningModeEnabled: PropTypes.bool,
  onSelectedDateChange: PropTypes.func.isRequired,
  onTaskBulkUnassigned: PropTypes.func.isRequired,
  loggedInUser: PropTypes.object.isRequired,
  handleBulkEdit: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  const { runData } = state.manageMapViewReducer;
  return {
    loggedInUser: state.appReducer.loggedInUser,
    runData,
  };
}

export default connect(mapStateToProps, null)(TaskHeader);
